import { z } from 'zod';

/**
 * `defer-llm-cost-telemetry` + Phase 8.10 — public shapes for the
 * per-call LLM telemetry table (`llm_calls`) and the admin
 * cost-aggregate API.
 *
 * Phase 8.10: nullable tokens (never fabricate), operation taxonomy,
 * correlationId, and normalized `AiUsageEvent` for internal writers.
 */

/**
 * Stable feature ids stored in `llm_calls.feature` (admin pivots).
 * Keep in sync with runtime-config `AiFeature` where they overlap.
 */
export const AI_FEATURE_IDS = [
  'intent',
  'explain',
  'healing',
  'debug',
  'rca',
  'suggestActions',
  'a11yFix',
  'agentExplore',
  'semanticAssert',
  'adversarialInputs',
  /** `defer-templates-ai-generated` (shipped 2026-05-20). */
  'templateGenerator',
  /** `defer-llm-tool-use` (shipped 2026-05-20) — generic function-call surface. */
  'toolUse',
  /** `defer-debug-vision` (shipped 2026-05-20) — screenshot extractor. */
  'vision',
  /** `pending-tag-search-saved-views-v2` — AI tag suggester (`POST /api/tags/suggest`). */
  'tagSuggest',
  /** Phase 6b — Custom Script "Generate with AI". */
  'scriptAuthor',
  /** Prod-error → draft Flow parser. */
  'prodError',
  /** Release readiness LLM summary. */
  'releaseVerdict',
  /** Flake detective hypothesis narrative. */
  'flakeHypothesis',
  /** Scenario AI drafts. */
  'scenarioDrafts',
  /** Scenario AI steps. */
  'scenarioSteps',
  /** Scenario failure-insight. */
  'failureAnalysis',
  /** Step NL parse (editor). */
  'stepParse',
  /** Element hint refine. */
  'elementHint',
  /** Story ingest audio transcription (Whisper). */
  'storyIngest',
  /** Analyze Application — AI Understanding (bounded pack). */
  'knowledgeAnalyze',
  /** Requirement Intelligence — requirement quality proposal. */
  'requirementIntelligence',
  /** Change Impact agent — narration over deterministic TIA findings. */
  'changeImpact',
  /** Story → business risks (Quality Intelligence). */
  'qualityRisk',
  /** Locator disambiguation tie-breaker (Healing / Runtime). */
  'locatorTiebreaker',
  /** Rynora Agent stub-flow authoring route. */
  'agent',
  'other',
] as const;
export type AiFeatureId = (typeof AI_FEATURE_IDS)[number];
export const AiFeatureIdSchema = z.enum(AI_FEATURE_IDS);

/**
 * Dotted operation taxonomy for cost/performance analysis (Phase 8.10).
 * Stored in `llm_calls.operation`. Free-form validated as non-empty string
 * so new ops can ship without a contracts bump blocking telemetry.
 */
export const AI_OPERATION_IDS = [
  'quality.scenario_generate',
  'quality.scenario_step_generate',
  'quality.failure_insight',
  'quality.flake_detection',
  'quality.release_verdict',
  'quality.knowledge_analyze',
  'quality.requirement_intelligence',
  'quality.change_impact_explain',
  'quality.fingerprint_remediation',
  'quality.story_audio_transcribe',
  'automation.intent_parse',
  'automation.intent_parse_stream',
  'legacy.scenario_automate',
  'automation.preview_run',
  'automation.explain',
  'automation.script_author',
  'automation.vision',
  'automation.healing_narrator',
  'automation.step_parse',
  'automation.element_hint',
  'automation.suggest_actions_polish',
  'automation.prod_error_parse',
  'automation.tool_use',
  'orphan.suggest',
  'orphan.rca',
  'orphan.semantic_assert',
  'orphan.adversarial',
  'orphan.explore',
] as const;
export type AiOperationId = (typeof AI_OPERATION_IDS)[number];
export const AiOperationIdSchema = z.enum(AI_OPERATION_IDS);

export const LLM_CALL_STATUSES = ['ok', 'error', 'cached', 'throttled'] as const;
export type LlmCallStatus = (typeof LLM_CALL_STATUSES)[number];
export const LlmCallStatusSchema = z.enum(LLM_CALL_STATUSES);

export const LLM_BYOK_SCOPES = ['user', 'org', 'platform'] as const;
export type LlmByokScope = (typeof LLM_BYOK_SCOPES)[number];
export const LlmByokScopeSchema = z.enum(LLM_BYOK_SCOPES);

/** Nullable token count — null means provider omitted usage (never invent). */
const NullableTokenSchema = z.number().int().nonnegative().nullable();

export const LlmCallRecordSchema = z.object({
  id: z.string().min(1),
  orgId: z.string().uuid().nullable(),
  userId: z.string().uuid().nullable(),
  feature: AiFeatureIdSchema,
  /** Dotted taxonomy; null on pre-8.10 rows. */
  operation: z.string().min(1).max(120).nullable().optional(),
  provider: z.string().min(1).max(40),
  model: z.string().min(1).max(120),
  inputTokens: NullableTokenSchema,
  outputTokens: NullableTokenSchema,
  totalTokens: NullableTokenSchema.optional(),
  /** null = unknown (tokens missing and/or model not in price book). Never invent $0. */
  costMicrosUsd: z.number().int().nonnegative().nullable(),
  /** True when cost came from price book + known tokens; false if unknown. */
  costEstimated: z.boolean().optional(),
  latencyMs: z.number().int().nonnegative(),
  status: LlmCallStatusSchema,
  byokScope: LlmByokScopeSchema.nullable(),
  correlationId: z.string().min(1).max(128).nullable().optional(),
  errorCategory: z.string().min(1).max(80).nullable().optional(),
  createdAt: z.string().datetime(),
});
export type LlmCallRecord = z.infer<typeof LlmCallRecordSchema>;

/**
 * Normalized internal writer shape (Phase 8.10). Metadata only —
 * never prompts, responses, keys, images, or tool arguments.
 */
export const AiUsageEventSchema = z.object({
  organizationId: z.string().uuid().nullable(),
  userId: z.string().uuid().nullable(),
  feature: AiFeatureIdSchema,
  operation: z.string().min(1).max(120),
  provider: z.string().min(1).max(40),
  model: z.string().min(1).max(120),
  correlationId: z.string().min(1).max(128).nullable(),
  latencyMs: z.number().int().nonnegative(),
  inputTokens: NullableTokenSchema,
  outputTokens: NullableTokenSchema,
  totalTokens: NullableTokenSchema,
  success: z.boolean(),
  errorCategory: z.string().min(1).max(80).nullable(),
  byokScope: LlmByokScopeSchema.nullable().optional(),
  createdAt: z.string().datetime().optional(),
});
export type AiUsageEvent = z.infer<typeof AiUsageEventSchema>;

/**
 * Aggregate row used by the admin "cost by feature/provider/model"
 * panel. Grouped server-side; the client renders a sortable table.
 *
 * Phase 8.19 — `costMicrosUsd` is null when every call in the bucket
 * has unknown cost (never invent $0). `hasUnknownCost` is true when at
 * least one call lacked a known cost (mixed buckets may still carry a
 * partial known sum).
 *
 * Token sums: null when every token side in the bucket is unknown.
 */
export const LlmCostAggregateRowSchema = z.object({
  feature: AiFeatureIdSchema,
  provider: z.string().min(1).max(40),
  model: z.string().min(1).max(120),
  calls: z.number().int().nonnegative(),
  inputTokens: z.number().int().nonnegative().nullable(),
  outputTokens: z.number().int().nonnegative().nullable(),
  /** null = all costs unknown in this bucket. */
  costMicrosUsd: z.number().int().nonnegative().nullable(),
  /** True when ≥1 call in the bucket had cost_micros_usd NULL. */
  hasUnknownCost: z.boolean(),
  errors: z.number().int().nonnegative(),
});
export type LlmCostAggregateRow = z.infer<typeof LlmCostAggregateRowSchema>;

export const LlmCostSummaryResponseSchema = z.object({
  /** UTC ISO date string (yyyy-mm-dd) marking the start of the window. */
  fromDate: z.string(),
  /** UTC ISO date string marking the (inclusive) end. */
  toDate: z.string(),
  totals: z.object({
    calls: z.number().int().nonnegative(),
    inputTokens: z.number().int().nonnegative().nullable(),
    outputTokens: z.number().int().nonnegative().nullable(),
    /** null when no known costs in the window (and calls > 0). Zero calls → 0. */
    costMicrosUsd: z.number().int().nonnegative().nullable(),
    hasUnknownCost: z.boolean(),
    errors: z.number().int().nonnegative(),
  }),
  byFeature: z.array(LlmCostAggregateRowSchema),
  byProviderModel: z.array(LlmCostAggregateRowSchema),
  recentCalls: z.array(LlmCallRecordSchema).max(200),
});
export type LlmCostSummaryResponse = z.infer<typeof LlmCostSummaryResponseSchema>;

/**
 * Per-(provider, model) price book. Operators can override via
 * admin config (Migration TBD — for v1 the platform default lives
 * in `llm-pricing.ts` and ships in the binary). Costs are USD
 * per million tokens. Missing combos → cost recorded as NULL micros
 * (unknown — never $0).
 */
export const LlmModelPriceSchema = z.object({
  provider: z.string().min(1).max(40),
  model: z.string().min(1).max(120),
  inputUsdPerMillion: z.number().nonnegative(),
  outputUsdPerMillion: z.number().nonnegative(),
});
export type LlmModelPrice = z.infer<typeof LlmModelPriceSchema>;
